/**
 * F6 (paridad Ledgeria) — Lo que el categorizador ha aprendido.
 *
 * Esta pantalla existe por una razón concreta: un clasificador que aprende y
 * no se puede auditar acaba metiendo gastos donde no van sin que nadie se
 * entere. Aquí se ve qué tipología lleva cada proveedor, cuántas veces se ha
 * confirmado, si la fijó una persona, y se puede corregir o borrar.
 */
import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, Brain, Loader2, Pin, RefreshCw, Trash2, Wand2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import { confirmDialog, toast } from '../../lib/dialogs'
import { currentLocaleTag } from '../../lib/formatting'
import { useAuth } from '../../contexts/AuthContext'

interface RuleDto {
  id: string
  partyKey: string
  supplierLabel: string
  categoryCode: string
  serviceLine: string | null
  hitCount: number
  source: string
  lastUsedAt: string
}

interface Proposal {
  expenseId: string
  reference: string
  supplierName: string
  from: string
  to: string
  source: string
  confidence: number
  reason: string
}

function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    return (err.body as { error?: string } | undefined)?.error ?? err.message
  }
  return err instanceof Error ? err.message : fallback
}

export default function ExpenseRulesSettingsPage() {
  const { t } = useTranslation(['settings', 'common'])
  const auth = useAuth()
  const canWrite = auth.status === 'authenticated' && auth.user.role === 'ADMIN'

  const [rules, setRules] = useState<RuleDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [proposals, setProposals] = useState<Proposal[] | null>(null)
  const [useAi, setUseAi] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const r = await api.get<{ items: RuleDto[] }>('/expense-rules')
      setRules(r.items)
    } catch (err) {
      setError(errorText(err, t('expenseRules.errors.load')))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void load()
  }, [load])

  const rebuild = async () => {
    if (!(await confirmDialog({ message: t('expenseRules.confirmRebuild'), tone: 'danger' }))) return
    setBusy(true)
    setError(null)
    try {
      const r = await api.post<{ suppliers: number; created: number; updated: number }>(
        '/expense-rules/rebuild',
        { dryRun: false }
      )
      toast.success(t('expenseRules.rebuildDone', { ...r }))
      await load()
    } catch (err) {
      setError(errorText(err, t('expenseRules.errors.generic')))
    } finally {
      setBusy(false)
    }
  }

  const forget = async (rule: RuleDto) => {
    if (
      !(await confirmDialog({
        message: t('expenseRules.confirmForget', { name: rule.supplierLabel }),
        tone: 'danger',
      }))
    )
      return
    try {
      await api.delete(`/expense-rules/${rule.id}`)
      await load()
    } catch (err) {
      setError(errorText(err, t('expenseRules.errors.generic')))
    }
  }

  const preview = async () => {
    setBusy(true)
    setError(null)
    try {
      const r = await api.post<{ proposals: Proposal[] }>('/expenses/recategorize', {
        dryRun: true,
        useAi,
        limit: 200,
      })
      setProposals(r.proposals)
    } catch (err) {
      setError(errorText(err, t('expenseRules.errors.generic')))
    } finally {
      setBusy(false)
    }
  }

  const apply = async () => {
    if (!proposals || proposals.length === 0) return
    if (!(await confirmDialog(t('expenseRules.confirmApply', { count: proposals.length })))) return
    setBusy(true)
    try {
      const r = await api.post<{ applied: number }>('/expenses/recategorize', {
        dryRun: false,
        useAi,
        limit: 200,
      })
      toast.success(t('expenseRules.applyDone', { count: r.applied }))
      setProposals(null)
      await load()
    } catch (err) {
      setError(errorText(err, t('expenseRules.errors.generic')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-serif font-semibold t-primary flex items-center gap-2">
            <Brain size={20} className="text-purple-600 dark:text-purple-300" />
            {t('expenseRules.title')}
          </h2>
          <p className="t-muted text-sm mt-1">{t('expenseRules.subtitle')}</p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={() => void rebuild()}
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm border border-base hover:bg-hover t-primary flex items-center gap-1.5 disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {t('expenseRules.rebuild')}
          </button>
        )}
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {/* Repaso del atraso: el sitio donde esto se paga solo. */}
      {canWrite && (
        <section className="surface p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold t-primary flex items-center gap-1.5">
                <Wand2 size={14} /> {t('expenseRules.recategorize.title')}
              </h3>
              <p className="text-xs t-muted">{t('expenseRules.recategorize.hint')}</p>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs t-muted flex items-center gap-1.5">
                <input type="checkbox" checked={useAi} onChange={(e) => setUseAi(e.target.checked)} />
                {t('expenseRules.recategorize.useAi')}
              </label>
              <button
                type="button"
                onClick={() => void preview()}
                disabled={busy}
                className="px-3 py-1.5 rounded-lg text-sm border border-base hover:bg-hover t-primary disabled:opacity-50"
              >
                {t('expenseRules.recategorize.preview')}
              </button>
              {proposals && proposals.length > 0 && (
                <button
                  type="button"
                  onClick={() => void apply()}
                  disabled={busy}
                  className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 disabled:opacity-50"
                >
                  {t('expenseRules.recategorize.apply', { count: proposals.length })}
                </button>
              )}
            </div>
          </div>

          {proposals && (
            proposals.length === 0 ? (
              <p className="text-xs t-muted">{t('expenseRules.recategorize.nothing')}</p>
            ) : (
              <ul className="space-y-1 max-h-72 overflow-y-auto text-sm">
                {proposals.map((p) => (
                  <li key={p.expenseId} className="flex items-start justify-between gap-3 px-2 py-1 rounded hover:bg-hover">
                    <div className="min-w-0">
                      <span className="font-mono text-xs t-muted mr-2">{p.reference}</span>
                      <span className="t-primary">{p.supplierName}</span>
                      <div className="text-[11px] t-faint">{p.reason}</div>
                    </div>
                    <div className="text-xs whitespace-nowrap shrink-0">
                      <span className="t-faint line-through">{p.from}</span>
                      <span className="mx-1.5 t-muted">→</span>
                      <span className="t-primary font-medium">{p.to}</span>
                      <span className="ml-2 t-faint">{Math.round(p.confidence * 100)}%</span>
                    </div>
                  </li>
                ))}
              </ul>
            )
          )}
        </section>
      )}

      <section className="surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs t-muted uppercase tracking-wider" style={{ borderBottom: '1px solid var(--border)' }}>
              <th className="px-3 py-2">{t('expenseRules.columns.supplier')}</th>
              <th className="px-3 py-2">{t('expenseRules.columns.category')}</th>
              <th className="px-3 py-2 text-center">{t('expenseRules.columns.hits')}</th>
              <th className="px-3 py-2">{t('expenseRules.columns.source')}</th>
              <th className="px-3 py-2">{t('expenseRules.columns.lastUsed')}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-xs t-muted">
                  <Loader2 size={14} className="animate-spin inline mr-1.5" />
                  {t('expenseRules.loading')}
                </td>
              </tr>
            ) : rules.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-xs t-muted">
                  {t('expenseRules.empty')}
                </td>
              </tr>
            ) : (
              rules.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }} className="t-primary">
                  <td className="px-3 py-1.5 max-w-xs truncate" title={r.supplierLabel}>
                    {r.supplierLabel}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs">{r.categoryCode}</td>
                  <td className="px-3 py-1.5 text-center text-xs t-muted">{r.hitCount}</td>
                  <td className="px-3 py-1.5">
                    {r.source === 'MANUAL' ? (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border bg-toga-500/15 text-toga-700 dark:text-toga-200 border-toga-500/30">
                        <Pin size={9} /> {t('expenseRules.sources.MANUAL')}
                      </span>
                    ) : (
                      <span className="text-[10px] t-faint">{t('expenseRules.sources.LEARNED')}</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-xs t-muted whitespace-nowrap">
                    {new Date(r.lastUsedAt).toLocaleDateString(currentLocaleTag())}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    {canWrite && (
                      <button
                        type="button"
                        onClick={() => void forget(r)}
                        title={t('expenseRules.forget')}
                        className="p-1 rounded hover:bg-hover text-rose-600 dark:text-rose-400"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
    </div>
  )
}
